/**
 * Dashboard Layout Component
 * Main layout wrapper with navbar and sidebar
 */

import React, { useState, useCallback, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import MsrInstallPrompt from "../msr/MsrInstallPrompt";

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";

const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  // Mobile off-canvas drawer open state (desktop ignores this).
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Close the mobile drawer on route change (belt-and-suspenders alongside the
  // per-link close, e.g. for programmatic navigation).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close the drawer when the viewport grows to desktop, so a mobile→desktop
  // resize can't leave a ghost drawer (or a stuck body-scroll lock) behind.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (mq.matches) {
        setMobileOpen(false);
      }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // While the drawer is open (mobile only — the hamburger is md:hidden): lock body
  // scroll behind the overlay and close on Escape.
  useEffect(() => {
    if (!mobileOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      <Navbar onMenuClick={() => setMobileOpen(true)} />
      <div className="flex w-full">
        {/* Mobile backdrop — tap to close the drawer */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            aria-hidden="true"
            onClick={() => setMobileOpen(false)}
          />
        )}
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleCollapsed}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <main className="flex-1 p-4 sm:p-6 min-w-0 overflow-x-auto">
          {/* MSR-only "Add to Home Screen" banner (renders nothing for other roles or on desktop). */}
          <MsrInstallPrompt />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
